/** Every word on the POS screen. Config key is `pos.<name>`; the app reports any that are missing. */
export const POS_LABEL_KEYS = [
    "title", "combos", "emptyMenu", "loading", "catalogFailed", "unavailable", "from",
    "ticket", "ticketEmpty", "items", "total", "estimateNote", "qtyUp", "qtyDown", "qty",
    "edit", "void", "sameAgain", "voidAsk", "voidYes", "voidNo", "voided", "cleared", "undo",
    "hold", "clear", "holdAsk", "clearAsk", "askYes", "askNo", "held", "recall", "repeatLast",
    "table", "customer", "comment", "commentHint", "quickTicket", "quickLine", "swaps", "noteLine",
    "required", "optional", "chooseOne", "pick", "lineTotal",
    "add", "update", "close", "back", "next", "step",
    "cash", "card", "cardOff", "later", "paying", "payEmpty",
    "offline", "retry", "cardUnavailable", "rejected", "unknown", "invalidPay",
    "placed", "paid", "unpaid", "newOrder", "serverTotal",
    "kwSwap", "kwNo", "kwExtra",
    "openDrawer", "printReceipt", "hardwareNoPrinter", "hardwareDone",
    "startOver", "startOverAsk", "startOverYes", "startOverNo",
    "itemOne", "ctxForHere", "ctxToGo", "ctxPayNow", "ctxPayLater", "ctxCallIn", "ctxOnline", "ctxTable", "ctxAdding", "ctxSoFar",
    "sendKitchen", "changeDue", "cardCancelled", "externalOrder", "discardAsk", "discardYes", "attrPath",
    "done", "attrPickOne", "attrPickUpTo", "attrPickAtLeast", "attrPickRange", "attrCollapse", "attrExpand",
];
/** English fallbacks for the edge-hardware labels when a config omits them. */
export const POS_HARDWARE_DEFAULTS = {
    openDrawer: "Open drawer",
    printReceipt: "Print receipt",
    hardwareNoPrinter: "No printer paired",
    hardwareDone: "Sent",
};
/** English fallbacks for the deep-attribute and Start over labels when a config omits them. */
export const POS_ATTRIBUTE_DEFAULTS = {
    startOver: "Cancel / Start over",
    startOverAsk: "Discard this order and start over?",
    startOverYes: "Yes, start over",
    startOverNo: "Keep order",
    done: "Done",
    itemOne: "1 item",
    discardAsk: "Discard this order?",
    discardYes: "Discard",
    attrPickOne: "Choose 1",
    attrPickUpTo: "Choose up to {max}",
    attrPickAtLeast: "Choose at least {min}",
    attrPickRange: "Choose {min} to {max}",
    attrPath: "Choices so far",
    attrCollapse: "Collapse",
    attrExpand: "Expand",
};
/** A label with its English fallback (config-less renders and tests stay readable). */
export const attrLabel = (l, k) => l[k] || POS_ATTRIBUTE_DEFAULTS[k];
/** Labels added in round 2 that no config carries yet: read with `extraLabel`, English fallback. */
export const POS_EXTRA_DEFAULTS = {
    notes: "Notes",
    moreActions: "More",
    openTicket: "Open ticket",
    closeTicket: "Close",
    needsTable: "needs table",
    tableHelper: "Table number?",
    tableCounter: "{n}/{max}",
    discardItemAsk: "Discard this item?",
    discardItemYes: "Discard",
    discardItemNo: "Keep",
    totalThisRound: "This round",
    totalTab: "Tab total",
    choosing: "choosing…",
    walkIn: "Walk-in",
    fulfilHelper: "Where is this order going?",
    fulfilForHere: "For here",
    fulfilToGo: "To go",
    tableAskTitle: "Table or tab number?",
    continue: "Continue",
    onTab: "On the tab",
    repeated: "Repeated #{n}",
    lineComment: "Comment",
    makeSeparate: "Make separate",
    customerOrOrder: "Customer name or order #",
};
/** Longest table label the backend accepts (QA-044). */
export const TABLE_LABEL_MAX = 24;
/** A round-2 label with its English fallback. */
export const extraLabel = (l, k) => l[k] || POS_EXTRA_DEFAULTS[k];
/** QA-087: the tab being added to, from the prop or (when core supplies it) the state. null = a normal ticket. */
export function posAddingTo(d) {
    return d.addingTo ?? d.state["addingTo"] ?? null;
}
/** QA-087: the tab's existing lines (empty when none or not adding). */
export function posExistingLines(d) {
    if (!posAddingTo(d))
        return [];
    const raw = d.existingLines ?? d.state["existingLines"];
    return Array.isArray(raw) ? raw : [];
}
/** QA-087: the repeated order number, from the prop or the state. */
export function posRepeatedNumber(d) {
    const v = d.repeatedNumber ?? d.state["repeatedNumber"];
    // The server's display number already carries "#"; the label adds its own, so strip one here ("Repeated ##1").
    return v === undefined || v === null ? "" : String(v).replace(/^#/, "");
}
/** The context chip with "Repeated #N" appended when the ticket was copied from an earlier order. */
export function posChipWithRepeat(chip, d) {
    const n = posRepeatedNumber(d);
    if (!n)
        return chip;
    const tag = fill(extraLabel(d.labels, "repeated"), { n });
    return chip ? `${chip} · ${tag}` : tag;
}
/** Fill `{n}` style placeholders. */
export function fill(tpl, vars) {
    return tpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ""));
}
/** "1 item" / "{n} items". */
export function itemsText(l, n) {
    return n === 1 ? l.itemOne || "1 item" : fill(l.items, { n });
}
/** Table label shown once: a bare number reads "Table 7"; any text (even "Table 4" or "Patio bar 7") is shown as typed (QA-044). */
export function posTableLabel(l, raw) {
    const t = raw.trim();
    if (!t)
        return "";
    return /^\d+$/.test(t) ? fill(l.ctxTable, { t }) : t;
}
/** Compact order-context line for the ticket header. */
export function posContextChip(c, l) {
    const table = c.table ? posTableLabel(l, c.table) : "";
    const parts = c.kind === "adding"
        ? [fill(l.ctxAdding, { n: (c.orderNumber ?? "").replace(/^#/, "") }), table, c.soFar ? fill(l.ctxSoFar, { amount: c.soFar }) : ""]
        : c.kind === "walk_in"
            ? [extraLabel(l, "walkIn"), c.fulfilment === "for_here" ? extraLabel(l, "fulfilForHere") : c.fulfilment === "to_go" ? extraLabel(l, "fulfilToGo") : "", table]
            : c.kind === "for_here"
                ? [l.ctxForHere, table]
                : c.kind === "to_go"
                    ? c.payLater ? [l.ctxCallIn, l.ctxPayLater] : [l.ctxToGo, l.ctxPayNow]
                    : [c.channelName ?? "", l.ctxOnline];
    return parts.filter(Boolean).join(" · ");
}
//# sourceMappingURL=posLabels.js.map